import { useParams, Link } from 'react-router-dom';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = getRelatedArticles(article.id, article.category, 3);

  const categoryLabel =
    article.category === 'movies' ? '电影' :
    article.category === 'tv' ? '电视剧' :
    article.category === 'variety' ? '综艺' :
    article.category === 'anime' ? '动漫' :
    article.category === 'documentary' ? '纪录片' : '平台资讯';

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.thumbnail,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.fullName,
      url: siteInfo.url,
    },
    url: `${siteInfo.url}/article/${article.id}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.id}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="container-main">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryLabel, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-10 bg-background-50">
          <div className="container-main max-w-4xl">
            <div className="mb-8">
              <div className="flex items-center gap-3 text-xs text-foreground-500 mb-4">
                <span className="px-2.5 py-0.5 bg-accent-100 text-accent-700 rounded font-medium">
                  {categoryLabel}
                </span>
                <span>{article.publishDate}</span>
                <span>{article.readTime}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views >= 10000
                    ? `${(article.views / 10000).toFixed(0)}万`
                    : article.views}{' '}
                  阅读
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 text-sm text-foreground-600">
                <span>作者：{article.author}</span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10 bg-background-200">
              <img
                src={article.thumbnail}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                prose-headings:text-foreground-900 prose-headings:font-bold
                prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-foreground-700 prose-p:leading-loose prose-p:mb-5
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center gap-2 mt-10 pt-8 border-t border-background-200/70 flex-wrap">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary-50 text-secondary-700 text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 bg-background-100">
            <div className="container-main">
              <h2 className="text-xl font-bold text-foreground-900 mb-6">相关文章</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-background-300 transition-all"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-background-200">
                      <img
                        src={ra.thumbnail}
                        alt={ra.title}
                        title={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}